import { useTranslation } from 'react-i18next'
import { Outlet, useMatch, useNavigate, useParams } from 'react-router'
import { useCallback, useMemo } from 'react'
import { EcommerceCommonHeader } from '@/components/shared/headers'
import { PageBody, PageHeader } from '@/components/shared/page-sections'
import { ScrollableRouteContainer } from '@/components/shared/Container'
import { useFinancialModelSettingsStore } from '@/feature/financial-model/settings/store/financialModelSettingsStore'
import { ModuleNavigation, TabValue } from '@/components/shared/ModuleNavigation'
import { useTwcStore } from '@/feature/financial-model/ecommerce/twc/store'
import { ViewSwitcher } from '@/components/shared/views-buttons'
import { ExportModuleDialog } from '@/feature/export/ecommerce/components/dowload'
import { Show } from '@/components/shared/show'
import { useGetSingleFinancialModelSettingsV2Query } from '@/feature/financial-model/api'
import { getTwcModuleCompletitionStatus } from '@/feature/financial-model/ecommerce/twc/utils'

const TwcRoute = () => {
  const { id: financialModelId } = useParams<{ id: string }>()
  const financialModelName = useFinancialModelSettingsStore(state => state.settings[financialModelId!].general?.name)
  const navigate = useNavigate()
  const handleTabsChange = useCallback(
    (value: TabValue) => {
      const tabNavigationMap: Record<TabValue, string> = {
        'set-up': `/dashboard/financial-model/${financialModelId}/ecommerce/trade-working-capital`,
        'historical-data': `/dashboard/financial-model/${financialModelId}/ecommerce/trade-working-capital/historical-data`,
        'forecast-data': `/dashboard/financial-model/${financialModelId}/ecommerce/trade-working-capital/forecast-data`,
        total: `/dashboard/financial-model/${financialModelId}/ecommerce/trade-working-capital/total`
      }
      navigate(tabNavigationMap[value])
    },
    [financialModelId, navigate]
  )

  const isTwcTotalsRoute = useMatch({
    path: `/dashboard/financial-model/${financialModelId}/ecommerce/trade-working-capital/total`
  })

  const { data: twcSettings, isLoading } = useGetSingleFinancialModelSettingsV2Query({
    id: financialModelId!,
    module: `twc`,
    section: `setup`
  })

  const isSetupCompleted = useMemo(
    () =>
      !isLoading &&
      [
        getTwcModuleCompletitionStatus(`receivables`, twcSettings?.twc.settings!),
        getTwcModuleCompletitionStatus(`payables`, twcSettings?.twc.settings!),
        getTwcModuleCompletitionStatus(`inventory`, twcSettings?.twc.settings!)
      ].some(status => status === `done`),
    [twcSettings, isLoading]
  )

  return (
    <ScrollableRouteContainer className={`p-0`}>
      <Show when={!!isTwcTotalsRoute} fallback={<SetupPageHeader financialModelName={financialModelName!} financialModelId={financialModelId!} />}>
        <TotalsPageHeader financialModelName={financialModelName!} financialModelId={financialModelId!} />
      </Show>

      <ModuleNavigation isSetupCompleted={isSetupCompleted && !isLoading} handleTabsChange={handleTabsChange} isTotalAllowed={isSetupCompleted}>
        <PageBody>
          <Outlet />
        </PageBody>
      </ModuleNavigation>
    </ScrollableRouteContainer>
  )
}

const SetupPageHeader = (props: { financialModelName: string; financialModelId: string }) => {
  const { t } = useTranslation(`twc`)
  const { financialModelName, financialModelId } = props
  return (
    <PageHeader className={`pb-0`}>
      <EcommerceCommonHeader linkText={t(`setup.header.back`)} path={`/dashboard/financial-model/${financialModelId}/ecommerce`} />
      <div className='mt-6'>
        <h1 className='page-title'>{t(`setup.header.title`)}</h1>
        <p className='page-subtitle'>{financialModelName}</p>
      </div>
    </PageHeader>
  )
}

const TotalsPageHeader = (props: { financialModelName: string; financialModelId: string }) => {
  const { t } = useTranslation(`twc`)
  const { financialModelName, financialModelId } = props

  const view = useTwcStore(state => state.view)
  const setView = useTwcStore(state => state.setView)

  const exportOptions = useMemo(
    () => ({
      module: `twc` as const,
      view
    }),
    [view]
  )
  return (
    <PageHeader>
      <EcommerceCommonHeader linkText={t(`common.header.back`)} path={`/dashboard/financial-model/${financialModelId}/ecommerce`} />
      <div className='mt-6 flex items-center justify-between'>
        <div>
          <h1 className='page-title'>{t(`total_results.header.title`)}</h1>
          <p className='page-subtitle'>{financialModelName}</p>
        </div>
        <div className='flex items-center gap-2'>
          <ViewSwitcher view={view} onViewChange={setView} />
          <ExportModuleDialog financialModelId={financialModelId!} options={exportOptions} />
        </div>
      </div>
    </PageHeader>
  )
}

export default TwcRoute
